import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaCertificate, FaAward, FaCheckCircle } from 'react-icons/fa';

const CertificationsSection = styled.section`
  padding: 120px 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.02) 0%, rgba(0, 212, 255, 0.02) 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.3), transparent);
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.8rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 4rem;
  background: linear-gradient(135deg, #ffffff 0%, #00d4ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, transparent, #00d4ff, transparent);
  }
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const CertificationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CertificationCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 2rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(0, 212, 255, 0.3);
    transform: translateY(-5px);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(135deg, #00d4ff, #0099cc);
  }
`;

const CertificationHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const CertificationIcon = styled.div`
  font-size: 2.5rem;
  color: #00d4ff;
`;

const CertificationInfo = styled.div`
  flex: 1;
`;

const CertificationTitle = styled.h3`
  font-size: 1.3rem;
  color: #ffffff;
  margin-bottom: 0.3rem;
`;

const CertificationIssuer = styled.p`
  color: #00d4ff;
  font-weight: 600;
  margin-bottom: 0.3rem;
`;

const CertificationDate = styled.p`
  color: #999999;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
`;

const CertificationDescription = styled.p`
  color: #cccccc;
  line-height: 1.6;
  font-size: 0.95rem;
  margin-bottom: 1rem;
`;

const CertificationDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const DetailTag = styled.span`
  background: rgba(0, 212, 255, 0.1);
  color: #00d4ff;
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
  border: 1px solid rgba(0, 212, 255, 0.3);
`;

const CertificationLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #00d4ff;
  font-size: 0.9rem;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    color: #ffffff;
    transform: translateX(3px);
  }
`;

const ValidityBadge = styled.div<{ valid: boolean }>`
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${props => props.valid ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)'};
  color: ${props => props.valid ? '#4caf50' : '#f44336'};
  border: 1px solid ${props => props.valid ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.3)'};
`;

const Certifications: React.FC = () => {
  const certifications = [
    {
      title: '정보처리기사',
      issuer: '한국산업인력공단',
      date: '2026년 6월',
      description: '소프트웨어 개발·운영에 필요한 정보처리 이론과 실무 능력을 검정하는 국가공인 자격증입니다. 필기 및 실기 시험에 최종 합격하여 정보 시스템 분석, 설계, 구현 및 운영 역량을 검증받았습니다.',
      details: ['정보처리', '소프트웨어 개발', '데이터베이스', '알고리즘', '네트워크', '최종 합격'],
      link: 'https://www.q-net.or.kr/man001.do?gubun=1',
      valid: true,
      icon: <FaCertificate />
    },
    {
      title: 'SQL 개발자 (SQLD)',
      issuer: '한국데이터산업진흥원',
      date: '2025년 9월',
      description: '데이터 모델링, 표준 SQL, 실행 계획·인덱스 기반 성능 최적화 등 핵심 DB 운용 역량을 검증하는 자격증입니다.',
      details: ['데이터 모델링', '정규화', '실행 계획 분석', 'SQL 튜닝', '인덱스/파티셔닝', 'ERD 설계', '트랜잭션/동시성'],
      link: 'https://www.dataq.or.kr/www/sub/a_04.do',
      valid: true,
      icon: <FaCertificate />
    },
    {
      title: '데이터분석 준전문가 (ADsP)',
      issuer: '한국데이터산업진흥원',
      date: '2025년 9월',
      description: '데이터 분석의 기초부터 고급 분석 기법까지 체계적으로 학습한 데이터 분석 전문가 자격증입니다. 데이터 수집, 전처리, 분석, 시각화 및 인사이트 도출 능력을 보유함을 증명합니다.',
      details: ['데이터 분석', '통계학', '데이터 시각화', '머신러닝', '빅데이터'],
      link: 'https://www.dataq.or.kr/www/sub/a_06.do',
      valid: true,
      icon: <FaCertificate />
    },
    {
      title: 'OPIc IM2',
      issuer: 'OPIc',
      date: '2026년 3월',
      description: '영어 말하기 능력을 평가하는 OPIc(오픽) 시험에서 IM2 등급을 취득했습니다. 실제 상황 기반 질문에 대해 논리적으로 답변하고, 일상·학업·업무 상황에서의 의사소통 역량을 검증했습니다.',
      details: ['영어 말하기', '의사소통', 'OPIc', 'IM2', 'Speaking'],
      link: 'https://www.opic.or.kr/',
      valid: true,
      icon: <FaCheckCircle />
    },
    {
      title: '컴퓨터활용능력 2급',
      issuer: '대한상공회의소',
      date: '2021년 11월',
      description: '컴퓨터와 정보화 사회에 대한 전반적인 이해와 컴퓨터 활용 능력을 인증받은 국가공인 자격증입니다. 사무자동화, 스프레드시트, 데이터베이스 활용 능력을 보유함을 증명합니다.',
      details: ['사무자동화', '스프레드시트', '데이터베이스', '컴퓨터 활용'],
      link: 'https://license.korcham.net',
      valid: true,
      icon: <FaCertificate />
    },
    {
      title: 'COS Pro 2급 (Python)',
      issuer: 'YBM',
      date: '2025년 1월',
      description: 'Python 프로그래밍 능력을 평가하는 국가공인 자격증입니다. Python 기초 문법부터 고급 프로그래밍 기법까지 체계적인 프로그래밍 능력을 보유함을 증명합니다.',
      details: ['Python', '프로그래밍', '알고리즘', '코딩'],
      link: 'https://www.ybmit.com/cos_pro/cos_pro_info.jsp',
      valid: true,
      icon: <FaAward />
    },
    {
      title: 'DIAT GOLD MASTER',
      issuer: '한국정보통신진흥협회',
      date: '2024년 11월',
      description: '디지털 정보 활용 능력을 종합적으로 평가하는 국가공인 자격증입니다. 컴퓨터 활용, 인터넷, 멀티미디어, 정보통신에 대한 전문 지식을 보유함을 증명합니다.',
      details: ['디지털 정보', '멀티미디어', '인터넷', '정보통신'],
      link: 'https://www.ihd.or.kr/memapplydiatskill.do',
      valid: true,
      icon: <FaCheckCircle />
    }
  ];

  return (
    <CertificationsSection id="certifications">
      <SectionTitle
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        Certifications
      </SectionTitle>
      
      <CertificationsGrid>
        {certifications.map((cert, index) => (
          <CertificationCard
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            viewport={{ once: true }}
          >
            <ValidityBadge valid={cert.valid}>
              {cert.valid ? '유효' : '만료'}
            </ValidityBadge>
            
            <CertificationHeader>
              <CertificationIcon>{cert.icon}</CertificationIcon>
              <CertificationInfo>
                <CertificationTitle>{cert.title}</CertificationTitle>
                <CertificationIssuer>{cert.issuer}</CertificationIssuer>
                <CertificationDate>{cert.date}</CertificationDate>
              </CertificationInfo>
            </CertificationHeader>
            
            <CertificationDescription>{cert.description}</CertificationDescription>
            
            <CertificationDetails>
              {cert.details.map((detail, detailIndex) => (
                <DetailTag key={detailIndex}>{detail}</DetailTag>
              ))}
            </CertificationDetails>
            
            <CertificationLink href={cert.link} target="_blank" rel="noopener noreferrer">
              자격증 확인하기 →
            </CertificationLink>
          </CertificationCard>
        ))}
      </CertificationsGrid>
    </CertificationsSection>
  );
};

export default Certifications;
